// ═══════════════════════════════════════════════════════════════════════════
// DrewsPickTab — admin curation surface (mini-spec #8).
//
// Lists recent high-scoring hooks (≥80) and lets Drew flip one to
// "Drew's Pick of the week". Calls the SECURITY DEFINER RPCs
// `set_drews_pick(hook_score_id, note)` and `clear_drews_pick(...)` —
// the RPC verifies admin status server-side, so even if the client
// bypassed auth, the DB rejects.
//
// Mounts inside AdminPage's tab strip.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { track, EVENTS } from '../../lib/analytics';

function thisWeekStart() {
  const now = new Date();
  // Monday-start ISO week, in UTC.
  const day = now.getUTCDay();
  const offset = (day === 0 ? -6 : 1 - day); // Sun→-6, Mon→0, …
  const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + offset));
  return monday.toISOString().slice(0, 10);
}

export default function DrewsPickTab() {
  const toast = useToast();
  const [busy, setBusy]           = useState(false);
  const [rows, setRows]           = useState([]);
  const [currentPick, setCurrent] = useState(null);
  const [search, setSearch]       = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const week = thisWeekStart();

  const refresh = useCallback(async () => {
    if (!supabase) return;
    setBusy(true);
    try {
      const [{ data: hooks }, { data: weekly }] = await Promise.all([
        supabase
          .from('hook_scores')
          .select('id, user_id, episode_id, hook_text, score, score_factors, niche, is_drews_pick, drews_pick_at, drews_pick_note, created_at')
          .gte('score', 80)
          .order('score', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(80),
        supabase
          .from('drews_picks_weekly')
          .select('hook_score_id, week_start, picked_at, note')
          .eq('week_start', week)
          .maybeSingle(),
      ]);
      setRows(hooks || []);
      setCurrent(weekly || null);
    } catch (err) {
      toast?.error?.(err?.message || 'Could not load picks.');
    } finally {
      setBusy(false);
    }
  }, [toast, week]);

  useEffect(() => { refresh(); }, [refresh]);

  async function setPick(row) {
    if (!supabase || !row?.id) return;
    setConfirmingId(null);
    setBusy(true);
    try {
      const { error } = await supabase.rpc('set_drews_pick', {
        p_hook_score_id: row.id,
        p_note:          noteDraft.trim() || null,
      });
      if (error) throw error;
      toast?.success?.('Pick saved. The user has been notified.');
      track(EVENTS.DREWS_PICK_SET, { hookScoreId: row.id, week });
      setNoteDraft('');
      await refresh();
    } catch (err) {
      toast?.error?.(err?.message || 'Could not set pick.');
    } finally {
      setBusy(false);
    }
  }

  async function clearPick(hookScoreId) {
    if (!supabase || !hookScoreId) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc('clear_drews_pick', {
        p_hook_score_id: hookScoreId,
      });
      if (error) throw error;
      toast?.success?.('Pick cleared for this week.');
      await refresh();
    } catch (err) {
      toast?.error?.(err?.message || 'Could not clear pick.');
    } finally {
      setBusy(false);
    }
  }

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (r.hook_text || '').toLowerCase().includes(q) ||
           (r.niche     || '').toLowerCase().includes(q);
  });

  return (
    <div className="drews-pick-tab">
      <div className="drews-pick-week-banner">
        <div>
          <div className="drews-pick-week-label">This week (starting {week} UTC)</div>
          <div className="drews-pick-week-current">
            {currentPick
              ? <>Picked: <code>{currentPick.hook_score_id}</code> {currentPick.note && <em>· {currentPick.note}</em>}</>
              : 'No pick set yet.'}
          </div>
        </div>
        {currentPick && (
          <button
            type="button"
            className="ws-btn-ghost"
            onClick={() => clearPick(currentPick.hook_score_id)}
            disabled={busy}
          >Clear this week's pick</button>
        )}
      </div>

      <div className="drews-pick-search-row">
        <input
          type="text"
          className="profile-editor-input"
          placeholder="Search hooks or niches…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <input
          type="text"
          className="profile-editor-input"
          placeholder="Optional pick note (visible to user)"
          value={noteDraft}
          onChange={e => setNoteDraft(e.target.value.slice(0, 200))}
        />
      </div>

      <div className="drews-pick-list">
        {filtered.length === 0 && !busy && (
          <div className="drews-pick-empty">No high-scoring hooks yet.</div>
        )}
        {filtered.map(r => (
          <div
            key={r.id}
            className={`drews-pick-row${r.is_drews_pick ? ' drews-pick-row--active' : ''}`}
          >
            <div className="drews-pick-row-score" data-tier={tierFor(r.score)}>
              {r.score}
            </div>
            <div className="drews-pick-row-body">
              <div className="drews-pick-row-text">"{r.hook_text}"</div>
              <div className="drews-pick-row-meta">
                {r.niche || 'general'} · {new Date(r.created_at).toLocaleDateString()}
                {r.is_drews_pick && (
                  <span className="drews-pick-row-active-tag">
                    Active pick {r.drews_pick_at && `· since ${new Date(r.drews_pick_at).toLocaleDateString()}`}
                  </span>
                )}
              </div>
            </div>
            <div className="drews-pick-row-actions">
              {confirmingId === r.id ? (
                <>
                  <button
                    type="button"
                    className="ws-btn-primary"
                    onClick={() => setPick(r)}
                    disabled={busy}
                  >Confirm</button>
                  <button
                    type="button"
                    className="ws-btn-ghost"
                    onClick={() => setConfirmingId(null)}
                    disabled={busy}
                  >Cancel</button>
                </>
              ) : (
                <button
                  type="button"
                  className="ws-btn-ghost"
                  onClick={() => setConfirmingId(r.id)}
                  disabled={busy}
                >{r.is_drews_pick ? 'Re-pick' : 'Set as pick'}</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function tierFor(score) {
  if (score >= 90) return 'gold';
  if (score >= 80) return 'green';
  if (score >= 60) return 'yellow';
  return 'red';
}
